import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import style from './color-picker.module.scss';

// Estimated popover height used to decide top-vs-bottom placement.
// Preset grid (10 rows × ~28px ≈ 290) + label gaps (~30) + custom row
// (~50) + paddings (~30) ≈ 400. Err on the generous side so the flip
// triggers early enough to keep the full palette visible inside modals.
const POPOVER_HEIGHT_ESTIMATE = 400;

// Curated brand color presets. 80 swatches arranged as 10 rows of 8,
// grouped by hue family so the grid reads as a palette rather than
// random swatches. Each row walks one slice of the wheel from light
// → mid → dark, so merchants can find both "soft pastel" and "deep
// brand" tones in the same row without hunting.
const DEFAULT_PRESETS = [
  // Row 1 — Neutrals (white → black)
  '#FFFFFF',
  '#F3F4F6',
  '#E5E7EB',
  '#9CA3AF',
  '#6B7280',
  '#374151',
  '#1F2937',
  '#000000',
  // Row 2 — Warm greys / browns
  '#F5F5F4',
  '#D6D3D1',
  '#A8A29E',
  '#78716C',
  '#57534E',
  '#44403C',
  '#3F2B23',
  '#2A1810',
  // Row 3 — Reds
  '#FECACA',
  '#FCA5A5',
  '#F87171',
  '#EF4444',
  '#DC2626',
  '#B91C1C',
  '#991B1B',
  '#7F1D1D',
  // Row 4 — Oranges
  '#FED7AA',
  '#FDBA74',
  '#FB923C',
  '#F97316',
  '#EA580C',
  '#C2410C',
  '#9A3412',
  '#7C2D12',
  // Row 5 — Yellows / Ambers
  '#FEF08A',
  '#FDE047',
  '#FACC15',
  '#EAB308',
  '#CA8A04',
  '#A16207',
  '#854D0E',
  '#713F12',
  // Row 6 — Greens / Limes
  '#BBF7D0',
  '#86EFAC',
  '#4ADE80',
  '#22C55E',
  '#16A34A',
  '#15803D',
  '#166534',
  '#14532D',
  // Row 7 — Teals / Cyans
  '#A7F3D0',
  '#6EE7B7',
  '#34D399',
  '#10B981',
  '#14B8A6',
  '#0D9488',
  '#0F766E',
  '#115E59',
  // Row 8 — Blues
  '#BAE6FD',
  '#7DD3FC',
  '#38BDF8',
  '#0EA5E9',
  '#3B82F6',
  '#2563EB',
  '#1D4ED8',
  '#1E3A8A',
  // Row 9 — Indigos / Violets
  '#C7D2FE',
  '#A5B4FC',
  '#818CF8',
  '#6366F1',
  '#8B5CF6',
  '#7C3AED',
  '#6D28D9',
  '#4C1D95',
  // Row 10 — Pinks / Magentas
  '#FBCFE8',
  '#F9A8D4',
  '#F472B6',
  '#EC4899',
  '#D946EF',
  '#C026D3',
  '#A21CAF',
  '#86198F',
];

const HEX_PATTERN = /^#([0-9A-F]{3}){1,2}$/i;
const normalizeHex = value => {
  if (!value) return '';
  const cleaned = value.trim().replace(/^#/, '');
  return `#${cleaned}`.toUpperCase();
};

function ColorPicker({ value, onChange, presets = DEFAULT_PRESETS, placeholder = 'Select color' }) {
  const [open, setOpen] = useState(false);
  const [hexDraft, setHexDraft] = useState(value || '');
  // Popover is rendered via portal into document.body so it can escape
  // clipping ancestors (e.g. modal bodies with overflow-y:auto). Coords
  // are tracked from the trigger's bounding rect; `placeAbove` flips
  // the popover upward when there's insufficient space below — same
  // pattern as DateRangeField / TimeRangeField.
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, placeAbove: false });
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const wasOpen = useRef(false);
  const spectrumInputId = useId();

  // Sync draft with incoming value so external changes are reflected.
  useEffect(() => {
    setHexDraft(value || '');
  }, [value]);

  // Close on click outside / ESC for keyboard users. With the popover
  // portaled to document.body, it lives outside containerRef — so the
  // outside-click check must also exclude the popover element itself.
  useEffect(() => {
    if (!open) return undefined;

    function onDocClick(e) {
      const insideTrigger = containerRef.current?.contains(e.target);
      const insidePopover = popoverRef.current?.contains(e.target);
      if (!insideTrigger && !insidePopover) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Return focus to the trigger when the popover closes.
  useEffect(() => {
    if (wasOpen.current && !open && triggerRef.current) {
      triggerRef.current.focus();
    }
    wasOpen.current = open;
  }, [open]);

  // Recompute portal coords whenever the popover opens, the viewport
  // resizes, or any ancestor scrolls — without this, scrolling the
  // modal body would leave the popover floating in place.
  useLayoutEffect(() => {
    if (!open) return undefined;

    function recompute() {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placeAbove = spaceBelow < POPOVER_HEIGHT_ESTIMATE && spaceAbove > spaceBelow;
      setCoords({
        top: placeAbove ? rect.top - 6 : rect.bottom + 6,
        left: rect.left,
        width: rect.width,
        placeAbove,
      });
    }

    recompute();
    // Capture-phase scroll listener catches scroll on any ancestor
    // (modal body, page) so the popover follows the trigger.
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [open]);

  function selectPreset(color) {
    onChange(color);
    setOpen(false);
  }

  function handleHexChange(e) {
    const next = e.target.value;
    setHexDraft(next);
    const normalized = normalizeHex(next);
    if (HEX_PATTERN.test(normalized)) {
      onChange(normalized);
    }
  }

  function handleNativeChange(e) {
    onChange(e.target.value.toUpperCase());
  }

  function handleClear() {
    onChange('');
    setHexDraft('');
  }

  const displayHex = value ? value.toUpperCase() : '';
  const isTransparent = displayHex === 'TRANSPARENT' || !displayHex;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        className={`${style.trigger} ${open ? style.open : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span
          className={`${style.swatch} ${isTransparent ? style.transparent : ''}`}
          style={{ backgroundColor: value || undefined }}
          aria-hidden="true"
        />
        {displayHex ? (
          <span className={style.hexText}>{displayHex}</span>
        ) : (
          <span className={style.placeholder}>{placeholder}</span>
        )}
        <svg
          className={`${style.chevron} ${open ? style.rotated : ''}`}
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popoverRef}
            className={`${style.popover} ${style.popoverPortal} ${
              coords.placeAbove ? style.popoverTop : ''
            }`}
            role="dialog"
            aria-label="Color picker"
            style={{
              top: coords.placeAbove ? 'auto' : `${coords.top}px`,
              bottom: coords.placeAbove ? `${window.innerHeight - coords.top}px` : 'auto',
              left: `${coords.left}px`,
            }}
          >
            <p className={style.popoverLabel}>Presets</p>
            <div className={style.presets}>
              {presets.map(color => (
                <button
                  key={color}
                  type="button"
                  className={`${style.presetSwatch} ${
                    displayHex === color.toUpperCase() ? style.selected : ''
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => selectPreset(color)}
                  aria-label={`Select ${color}`}
                />
              ))}
            </div>

            <p className={style.popoverLabel}>Custom</p>
            <div className={style.customSection}>
              {/* Spectrum bar — a full-width rainbow band that doubles
                  as the trigger for the native color picker. Wider +
                  labeled + dropper icon makes the interaction obvious:
                  "click this to pick any color from the spectrum",
                  rather than the small chip-with-corner-badge that
                  merchants kept overlooking. */}
              <label
                className={style.spectrumButton}
                title="Open color spectrum"
                htmlFor={spectrumInputId}
              >
                <svg
                  className={style.spectrumIcon}
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M11.5 1.5l3 3-7.5 7.5H4v-3l7.5-7.5z"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 14h12"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
                <span className={style.spectrumLabel}>Pick a custom color</span>
                <input
                  id={spectrumInputId}
                  type="color"
                  className={style.spectrumInput}
                  value={displayHex && HEX_PATTERN.test(displayHex) ? displayHex : '#000000'}
                  onChange={handleNativeChange}
                  aria-label="Open system color picker"
                />
              </label>
              <div className={style.customInputRow}>
                <span
                  className={`${style.swatch} ${isTransparent ? style.transparent : ''}`}
                  style={{ backgroundColor: value || undefined }}
                  aria-hidden="true"
                />
                <input
                  type="text"
                  className={style.hexInput}
                  value={hexDraft}
                  onChange={handleHexChange}
                  placeholder="#000000"
                  maxLength={7}
                  spellCheck={false}
                  aria-label="Hex color code"
                />
                {value && (
                  <button type="button" className={style.clearBtn} onClick={handleClear}>
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

ColorPicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  presets: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
};

export default ColorPicker;
